import _ from "lodash";
import { IClientConfig } from "src/client-config/config";
import { BetTypeEnum } from "src/constants/enums/betType";
import { ISelection } from "src/constants/interfaces/bet";
import { IMainStore } from "src/stores/main";
import { IUpperDataStore } from "src/stores/upperData";
import { composeAND, composeOR } from "../strategy";
import { isTerminal } from "src/common/helpers/productType";
import { getBetMaxStake, getBetMinStake } from "src/common/helpers/restrictions";
import { BetCalculationsEnum, getBetCalculations } from "calculation";
import {
  getEffectiveSelectionsCount,
  hasExpressIdIncompatibility,
  hasIncompleteBetBuilderCard,
} from "src/common/helpers/betBuilder";

export const placeBetDisabledRestriction = (config: IClientConfig) => !!config.restrictions?.disablePlaceBet;

export const invalidForecastSelectionsCount = (
  config: IClientConfig,
  selections: ISelection[],
  mainState: IMainStore
) =>
  [BetTypeEnum.StraightForecast, BetTypeEnum.ReverseForecast, BetTypeEnum.CombinationForecast].includes(
    mainState.betType
  ) && selections.length < 2;

export const invalidTricastSelectionsCount = (config: IClientConfig, selections: ISelection[], mainState: IMainStore) =>
  [BetTypeEnum.StraightTricast, BetTypeEnum.CombinationTricast].includes(mainState.betType) && selections.length < 3;

export const invalidSingleSelectionsCount = (config: IClientConfig, selections: ISelection[], mainState: IMainStore) =>
  !config.enableSingles && mainState.betType === BetTypeEnum.Single && getEffectiveSelectionsCount(selections) > 1;

export const invalidMultipleSelectionsCount = (
  config: IClientConfig,
  selections: ISelection[],
  mainState: IMainStore
) => mainState.betType === BetTypeEnum.Multiple && getEffectiveSelectionsCount(selections) < 2;

export const invalidSystemSelectionsCount = (config: IClientConfig, selections: ISelection[], mainState: IMainStore) =>
  mainState.betType === BetTypeEnum.System && getEffectiveSelectionsCount(selections) < 3;

// Place-bet block when a bet builder card has only a single selection — the
// user started a card but hasn't completed the combination yet.
export const incompleteBetBuilderCard = (config: IClientConfig, selections: ISelection[]) =>
  hasIncompleteBetBuilderCard(selections);

// Place-bet block when a regular selection conflicts on `express_id` with
// an existing bet builder card from the same game. The sportsbook UI no
// longer blocks the click — the betslip is the place where this conflict
// surfaces (notify message + place-bet disabled).
export const expressIdIncompatibility = (config: IClientConfig, selections: ISelection[]) =>
  hasExpressIdIncompatibility(selections);

// Shape of the betBuilderStore state we care about for place-bet validation.
// Keeping this local avoids a circular import between the rules layer and
// the stores layer (stores depend on rules-related types in places).
export interface IBetBuilderValidationState {
  groups: Record<number, { combinedPrice: number; loading: boolean }>;
  totalOdd: number;
}

// True when ANY bet builder group is currently fetching odds. While a card
// is "processing" we don't yet know if the combination is valid or what its
// price is, so placing the bet is unsafe.
export const betBuilderOddsLoading = (
  selections: ISelection[],
  bbState?: IBetBuilderValidationState,
): boolean => {
  if (!bbState) return false;
  if (!selections.some((s) => s.event.isInternalBetBuilder)) return false;
  return Object.values(bbState.groups).some((g) => g.loading);
};

// True when there's at least one bet builder card in the slip and that card
// has no combined price yet (server didn't return an odd, or returned 0).
// Also catches the case where every group is settled but `totalOdd` is 0.
// Loading groups are skipped here — `betBuilderOddsLoading` covers them.
export const betBuilderMissingTotalOdd = (
  selections: ISelection[],
  bbState?: IBetBuilderValidationState,
): boolean => {
  if (!bbState) return false;
  const bbGameIds = new Set<number>();
  selections.forEach((s) => {
    if (s.event.isInternalBetBuilder) bbGameIds.add(s.game.id as number);
  });
  if (!bbGameIds.size) return false;

  for (const gameId of bbGameIds) {
    const group = bbState.groups[gameId];
    // Settled (not loading) but no price — invalid card.
    if (!group) return true;
    if (group.loading) continue;
    if (!group.combinedPrice || group.combinedPrice <= 0) return true;
  }
  return false;
};

export const stakeIsEmpty = (config: IClientConfig, selections: ISelection[], mainState: IMainStore) =>
  config.enableSingleStakeInput && mainState.betType === BetTypeEnum.Single
    ? selections.some((item) => !+(item.singleStake || ""))
    : !+mainState.stake;

export const insufficientBalance = (
  config: IClientConfig,
  selections: ISelection[],
  mainState: IMainStore,
  upperData: IUpperDataStore
) =>
  config.enableSingleStakeInput && mainState.betType === BetTypeEnum.Single
    ? _.sum(selections.map((item) => +(item.singleStake || ""))) > upperData.balance
    : +mainState.stake > upperData.balance;

export const hasNotLoadedSelections = (config: IClientConfig, selections: ISelection[]) =>
  // Bet builder selections are validated via get_bet_builder_odds and don't
  // subscribe to per-event swarm updates, so `loaded` is never set on them.
  // Skip them here to avoid false "events not available" warnings.
  selections.some((item) => !item.loaded && !item.event.isInternalBetBuilder);

export const hasInactiveSelections = (config: IClientConfig, selections: ISelection[]) =>
  selections.some((item) => item.game.is_blocked || item.inactive);

export const minStakeNotReached = (
  config: IClientConfig,
  selections: ISelection[],
  mainState: IMainStore,
  upperData: IUpperDataStore
) => {
  const isSingles = config.enableSingleStakeInput && mainState.betType === BetTypeEnum.Single;

  const minStake = getBetMinStake(config, selections, mainState, upperData);

  if (!minStake) return false;

  return isSingles ? selections.some((item) => +(item.singleStake || "") < minStake) : +mainState.stake < minStake;
};

export const notFreeBet = (
  config: IClientConfig,
  selections: ISelection[],
  mainState: IMainStore,
  upperData: IUpperDataStore,
) => !mainState.isFreeBetEnabled || !mainState.freeBets.some((freeBet) => freeBet.selected);

export const maxStakeExceeded = (config: IClientConfig, selections: ISelection[], mainState: IMainStore) => {
  const isSingles = config.enableSingleStakeInput && mainState.betType === BetTypeEnum.Single;
  const maxStake = getBetMaxStake(config, mainState);

  return isSingles ? selections.some((item) => +(item.singleStake || "") > maxStake) : +mainState.stake > maxStake;
};

export const notSuperbet = (config: IClientConfig, selections: ISelection[], mainState: IMainStore) =>
  !mainState.isSuperbet;

export const maxStakeLimitReached = composeAND(maxStakeExceeded, notSuperbet);

export const stakeOutOfLimits = composeOR(minStakeNotReached, maxStakeLimitReached);

export const possibleWinOutOfLimits = (
  config: IClientConfig,
  selections: ISelection[],
  mainState: IMainStore,
  upperData: IUpperDataStore
) => {
  const { betType, stake, isSuperbet, systemCombinationCount } = mainState;
  const { oddsFormat, partnerConfig, currencyConfig } = upperData;
  const { max_payout = Number.MAX_SAFE_INTEGER } = partnerConfig || {};

  const { possibleWin } = getBetCalculations([BetCalculationsEnum.PossibleWin], {
    config,
    oddsFormat,
    partnerConfig,
    betType,
    stake: +stake,
    events: selections,
    systemCombinationCount,
    currencyConfig,
  });

  if (possibleWin > max_payout) return config.superbetHiddenMode || !isSuperbet;

  return false;
};

export const invalidBalance = composeAND(insufficientBalance, isTerminal, notFreeBet);

export const invalidFreeBet = (config: IClientConfig, selections: ISelection[], mainState: IMainStore) => {
  const selectedFreeBet = mainState.freeBets.find(item => item.selected);
  return mainState.isFreeBetEnabled && (!selectedFreeBet || selectedFreeBet.disabled);
};

export const invalidMinOdd = (config: IClientConfig, selections: ISelection[], mainState: IMainStore) => {
  if (mainState.betType === BetTypeEnum.System || !config.restrictions?.minimalOdd) return false;

  const sumOfPrice = selections.reduce((acc: number, selection: ISelection) => acc * selection.event.price, 1);

  return sumOfPrice < config.restrictions?.minimalOdd;
};
